// Bandeja de Revisiones pendientes (Módulo G, RF-G1) sobre TanStack Query.
// Mismo patrón que `hooks/useInformes.ts`: la cache de la query es la fuente
// de verdad de la lista.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { INFORMES_KEY, informeKey } from "@/hooks/useInformes";
import {
  aprobarCotizacionRequest,
  fetchRevisionesPendientes,
  fetchRevisoresDeInformes,
  type FiltrosDeRevisiones,
} from "@/lib/revisiones";

export const REVISIONES_KEY = ["revisiones-pendientes"] as const;
export const REVISORES_KEY = ["revisiones-pendientes", "revisores"] as const;

/**
 * La cola de revisiones pendientes.
 *
 * El filtro va DENTRO de la queryKey: cada combinación es una respuesta
 * distinta del servidor —filtra, ordena y pagina el backend— y con una key
 * única la cache las guarda por separado.
 *
 * `placeholderData` conserva la página anterior mientras llega la nueva: sin
 * él, cambiar de revisor o de página vacía la tabla y la pantalla salta a los
 * esqueletos en cada clic.
 */
export function useRevisionesPendientes(filtros: FiltrosDeRevisiones = {}) {
  return useQuery({
    queryKey: [...REVISIONES_KEY, filtros],
    queryFn: () => fetchRevisionesPendientes(filtros),
    placeholderData: (anterior) => anterior,
  });
}

/**
 * Las opciones del filtro por revisor.
 *
 * `staleTime` alto por lo mismo que los catálogos del informe: quién es
 * revisor cambia cuando alguien edita un informe, no mientras se mira esta
 * pantalla. Sin él, cada vez que la ventana recupera el foco se dispara un
 * refetch que puede reordenar el select bajo el cursor.
 *
 * `habilitado` existe porque el filtro es SOLO del Super Admin (RF-G1): al
 * resto la bandeja ya le llega acotada a lo suyo, así que pedir el catálogo
 * sería una consulta para un select que no se dibuja.
 */
export function useRevisoresDeInformes({ habilitado = true }: { habilitado?: boolean } = {}) {
  return useQuery({
    queryKey: REVISORES_KEY,
    queryFn: fetchRevisoresDeInformes,
    enabled: habilitado,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Aprobar la cotización y enviarla al cliente (RF-G3).
 *
 * Invalida TRES cosas y ninguna sobra: la ficha del informe —cuyo estado
 * cambió—, la bandeja —de la que este informe acaba de salir— y el listado de
 * Informes en proceso, donde el badge de estado es distinto.
 *
 * Invalida en vez de sembrar: la respuesta es la COTIZACIÓN, no el informe
 * completo, así que escribirla en la cache de la ficha la dejaría con la forma
 * equivocada. Mismo criterio que `useCrearCotizacion`.
 *
 * La variable de la mutación son las direcciones en COPIA del correo al
 * cliente. Van como parámetro y no dentro del hook porque las escribe quien
 * aprueba, en la modal de confirmación, y cambian en cada envío.
 */
export function useAprobarCotizacion(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (copias: string[] = []) => aprobarCotizacionRequest(informeId, copias),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: informeKey(informeId) });
      queryClient.invalidateQueries({ queryKey: REVISIONES_KEY });
      queryClient.invalidateQueries({ queryKey: INFORMES_KEY });
    },
  });
}
